/**
 * ProjectTimeline Component
 *
 * Displays project milestones in a visual timeline.
 * Allows tracking of key project phases and deadlines.
 */

import { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Calendar,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  XCircle,
  Plus,
  CalendarDays,
  Flag,
  Milestone,
  Target,
  Loader2,
} from 'lucide-react';
import type {
  ProjectMilestone,
  MilestoneStatus,
  Unit,
  ProjectSegment,
} from '@/types/project';

interface ProjectTimelineProps {
  projectId: string;
  units: Unit[];
  segments: ProjectSegment[];
  milestones: ProjectMilestone[];
  isLoading?: boolean;
  onCreateMilestone?: (milestone: Omit<ProjectMilestone, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  onUpdateMilestone?: (milestoneId: string, updates: Partial<ProjectMilestone>) => Promise<void>;
}

// Status configuration
const STATUS_CONFIG: Record<MilestoneStatus, { label: string; icon: typeof Circle; color: string }> = {
  pending: { label: 'Pending', icon: Circle, color: 'text-gray-400' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-blue-500' },
  complete: { label: 'Complete', icon: CheckCircle2, color: 'text-green-500' },
  delayed: { label: 'Delayed', icon: AlertTriangle, color: 'text-yellow-500' },
  blocked: { label: 'Blocked', icon: XCircle, color: 'text-red-500' },
};

// Category labels
const CATEGORY_LABELS: Record<string, string> = {
  permitting: 'Permitting',
  construction: 'Construction',
  inspection: 'Inspection',
  administrative: 'Administrative',
  other: 'Other',
};

// Format date
function formatDate(date: any): string {
  if (!date) return 'Not set';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Check if date is overdue
function isOverdue(targetDate: any): boolean {
  if (!targetDate) return false;
  const d = targetDate.toDate ? targetDate.toDate() : new Date(targetDate);
  return d < new Date();
}

// Default milestones for new projects
const DEFAULT_MILESTONES = [
  { name: 'Site Plan Approval', category: 'permitting', sortOrder: 1 },
  { name: 'Building Permits Submitted', category: 'permitting', sortOrder: 2 },
  { name: 'Building Permits Issued', category: 'permitting', sortOrder: 3 },
  { name: 'Foundation Complete', category: 'construction', sortOrder: 4 },
  { name: 'Rough-In Inspections', category: 'inspection', sortOrder: 5 },
  { name: 'Final Inspections', category: 'inspection', sortOrder: 6 },
  { name: 'Certificates of Occupancy', category: 'administrative', sortOrder: 7 },
];

export function ProjectTimeline({
  projectId,
  units,
  segments,
  milestones,
  isLoading = false,
  onCreateMilestone,
  onUpdateMilestone,
}: ProjectTimelineProps) {
  const { toast } = useToast();

  // Local state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    name: '',
    description: '',
    category: 'permitting' as ProjectMilestone['category'],
    targetDate: '',
    scope: 'project' as ProjectMilestone['scope'],
    segmentId: '',
    unitId: '',
  });

  // Sort milestones by sortOrder
  const sortedMilestones = useMemo(() => {
    return [...milestones].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [milestones]);

  // Calculate completion percentage
  const completionPercent = useMemo(() => {
    if (milestones.length === 0) return 0;
    const completed = milestones.filter((m) => m.status === 'complete').length;
    return Math.round((completed / milestones.length) * 100);
  }, [milestones]);

  // Handle add milestone
  const handleAddMilestone = useCallback(async () => {
    if (!newMilestone.name.trim() || !onCreateMilestone) {
      toast({
        title: 'Error',
        description: 'Milestone name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const milestone: Omit<ProjectMilestone, 'id' | 'createdAt' | 'updatedAt'> = {
        projectId,
        name: newMilestone.name,
        description: newMilestone.description || undefined,
        category: newMilestone.category,
        targetDate: newMilestone.targetDate ? new Date(newMilestone.targetDate) : undefined,
        status: 'pending',
        scope: newMilestone.scope,
        segmentId: newMilestone.scope === 'segment' ? newMilestone.segmentId : undefined,
        unitId: newMilestone.scope === 'unit' ? newMilestone.unitId : undefined,
        sortOrder: milestones.length + 1,
      };

      await onCreateMilestone(milestone);

      toast({
        title: 'Milestone added',
        description: `${newMilestone.name} has been added to the timeline`,
      });

      setShowAddDialog(false);
      setNewMilestone({
        name: '',
        description: '',
        category: 'permitting',
        targetDate: '',
        scope: 'project',
        segmentId: '',
        unitId: '',
      });
    } catch (err) {
      console.error('Error adding milestone:', err);
      toast({
        title: 'Error',
        description: 'Failed to add milestone',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [newMilestone, projectId, milestones.length, onCreateMilestone, toast]);

  // Handle status update
  const handleStatusChange = useCallback(
    async (milestoneId: string, status: MilestoneStatus) => {
      if (!onUpdateMilestone) return;

      try {
        const updates: Partial<ProjectMilestone> = { status };
        if (status === 'complete') {
          updates.completedDate = new Date();
        }
        await onUpdateMilestone(milestoneId, updates);
      } catch (err) {
        console.error('Error updating milestone:', err);
        toast({
          title: 'Error',
          description: 'Failed to update milestone',
          variant: 'destructive',
        });
      }
    },
    [onUpdateMilestone, toast]
  );

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                Project Timeline
              </CardTitle>
              <CardDescription>
                {milestones.length} milestone{milestones.length !== 1 ? 's' : ''} •{' '}
                {completionPercent}% complete
              </CardDescription>
            </div>
            {onCreateMilestone && (
              <Button onClick={() => setShowAddDialog(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Milestone
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sortedMilestones.length === 0 ? (
            <div className="text-center py-8">
              <Milestone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No Milestones Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add milestones to track key project phases and deadlines.
              </p>
              {onCreateMilestone && (
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Milestone
                </Button>
              )}
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-muted" />

              {/* Milestones */}
              <div className="space-y-4">
                {sortedMilestones.map((milestone, index) => {
                  const StatusIcon = STATUS_CONFIG[milestone.status].icon;
                  const statusColor = STATUS_CONFIG[milestone.status].color;
                  const overdue =
                    milestone.status !== 'complete' && isOverdue(milestone.targetDate);

                  return (
                    <div key={milestone.id} className="relative flex gap-4">
                      {/* Status indicator */}
                      <div
                        className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          milestone.status === 'complete'
                            ? 'bg-green-100 dark:bg-green-950'
                            : overdue
                            ? 'bg-red-100 dark:bg-red-950'
                            : 'bg-muted'
                        }`}
                      >
                        <StatusIcon
                          className={`h-5 w-5 ${overdue ? 'text-red-500' : statusColor}`}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{milestone.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {CATEGORY_LABELS[milestone.category]}
                              </Badge>
                              {milestone.targetDate && (
                                <span
                                  className={`text-xs flex items-center gap-1 ${
                                    overdue ? 'text-red-500' : 'text-muted-foreground'
                                  }`}
                                >
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(milestone.targetDate)}
                                  {overdue && ' (Overdue)'}
                                </span>
                              )}
                              {milestone.completedDate && (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Completed {formatDate(milestone.completedDate)}
                                </span>
                              )}
                            </div>
                            {milestone.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {milestone.description}
                              </p>
                            )}
                          </div>

                          {/* Status dropdown */}
                          {onUpdateMilestone && (
                            <Select
                              value={milestone.status}
                              onValueChange={(value: MilestoneStatus) =>
                                handleStatusChange(milestone.id, value)
                              }
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="complete">Complete</SelectItem>
                                <SelectItem value="delayed">Delayed</SelectItem>
                                <SelectItem value="blocked">Blocked</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Milestone Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
            <DialogDescription>
              Add a new milestone to track project progress.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label htmlFor="milestone-name">Name *</Label>
              <Input
                id="milestone-name"
                value={newMilestone.name}
                onChange={(e) =>
                  setNewMilestone((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g., Building Permits Issued"
              />
            </div>

            {/* Category */}
            <div>
              <Label htmlFor="milestone-category">Category</Label>
              <Select
                value={newMilestone.category}
                onValueChange={(value: ProjectMilestone['category']) =>
                  setNewMilestone((prev) => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="permitting">Permitting</SelectItem>
                  <SelectItem value="construction">Construction</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="administrative">Administrative</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Target Date */}
            <div>
              <Label htmlFor="milestone-date">Target Date</Label>
              <Input
                id="milestone-date"
                type="date"
                value={newMilestone.targetDate}
                onChange={(e) =>
                  setNewMilestone((prev) => ({ ...prev, targetDate: e.target.value }))
                }
              />
            </div>

            {/* Scope */}
            <div>
              <Label htmlFor="milestone-scope">Scope</Label>
              <Select
                value={newMilestone.scope}
                onValueChange={(value: ProjectMilestone['scope']) =>
                  setNewMilestone((prev) => ({ ...prev, scope: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Entire Project</SelectItem>
                  {segments.length > 0 && <SelectItem value="segment">Segment/Phase</SelectItem>}
                  {units.length > 0 && <SelectItem value="unit">Specific Unit</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* Segment selection */}
            {newMilestone.scope === 'segment' && segments.length > 0 && (
              <div>
                <Label htmlFor="milestone-segment">Segment</Label>
                <Select
                  value={newMilestone.segmentId}
                  onValueChange={(value) =>
                    setNewMilestone((prev) => ({ ...prev, segmentId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {segments.map((seg) => (
                      <SelectItem key={seg.id} value={seg.id}>
                        {seg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Unit selection */}
            {newMilestone.scope === 'unit' && units.length > 0 && (
              <div>
                <Label htmlFor="milestone-unit">Unit</Label>
                <Select
                  value={newMilestone.unitId}
                  onValueChange={(value) =>
                    setNewMilestone((prev) => ({ ...prev, unitId: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div>
              <Label htmlFor="milestone-description">Description (Optional)</Label>
              <Input
                id="milestone-description"
                value={newMilestone.description}
                onChange={(e) =>
                  setNewMilestone((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Brief description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAddMilestone} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
